// Questionnaire select primitive with the permission-gated inline
// "add unlisted option" affordance (spec [D5]) and the date field with an
// inline calendar. Pure logic lives in shell-field-primitives-core.ts.

import { useMemo, useState } from "react";
import { Pressable, Text, TextInput, View } from "react-native";

import type { AppShellTheme } from "./shell-core.js";
import { LineIcon } from "./line-icons.js";
import {
  buildMonthGrid,
  formatMonthTitleText,
} from "./clinician/list-calendar.js";
import {
  filterQuestionnaireSelectOptions,
  hydrateQuestionnaireSelectValues,
  parseIsoDateText,
  type QuestionnaireSelectOption,
  type QuestionnaireSelectValue,
} from "./shell-field-primitives-core.js";

export {
  filterQuestionnaireSelectOptions,
  hydrateQuestionnaireSelectValues,
  parseIsoDateText,
  type QuestionnaireSelectOption,
  type QuestionnaireSelectValue,
};

export interface QuestionnaireDateFieldProps {
  label: string;
  value: string;
  theme: AppShellTheme;
  onCommit: (text: string) => void;
  testID?: string;
}

/** Date answer input: free typing (committed on blur/submit, same contract as
 *  plain text rows) plus an inline-expanding month calendar — the same
 *  no-overlay pattern the DOS list calendar uses, so it works identically
 *  inside modals and scroll views on web and native. */
export function QuestionnaireDateField(
  props: QuestionnaireDateFieldProps,
): React.JSX.Element {
  const { label, value, theme, onCommit, testID } = props;
  const [draft, setDraft] = useState(value);
  const [openMonth, setOpenMonth] = useState<{
    year: number;
    monthIndex: number;
  } | null>(null);
  // Rebuilding the 6x7 grid (42 Date mutations) on every keystroke in the
  // adjacent text input is per-keystroke jank on low-end tablets.
  const calendarWeeks = useMemo(
    () => (openMonth ? buildMonthGrid(openMonth.year, openMonth.monthIndex) : null),
    [openMonth],
  );

  const selectedParts = parseIsoDateText(draft);
  const selectedIso = selectedParts?.isoDate ?? null;

  const toggleCalendar = () => {
    if (openMonth) {
      setOpenMonth(null);
      return;
    }
    const today = new Date();
    setOpenMonth(
      selectedParts
        ? { year: selectedParts.year, monthIndex: selectedParts.monthIndex }
        : { year: today.getFullYear(), monthIndex: today.getMonth() },
    );
  };

  const shiftMonth = (offset: number) => {
    if (!openMonth) return;
    const next = new Date(Date.UTC(openMonth.year, openMonth.monthIndex + offset, 1));
    setOpenMonth({ year: next.getUTCFullYear(), monthIndex: next.getUTCMonth() });
  };

  const pickDay = (isoDate: string) => {
    setDraft(isoDate);
    onCommit(isoDate);
    setOpenMonth(null);
  };

  return (
    <View testID={testID}>
      <View style={{ alignItems: "center", flexDirection: "row", gap: 6 }}>
        <TextInput
          value={draft}
          onChangeText={setDraft}
          onBlur={() => onCommit(draft)}
          onSubmitEditing={() => onCommit(draft)}
          placeholder="YYYY-MM-DD"
          placeholderTextColor={theme.textTertiary}
          accessibilityLabel={label}
          style={{
            borderWidth: 1,
            borderColor: theme.borderStrong,
            backgroundColor: theme.surface,
            color: theme.textPrimary,
            borderRadius: 8,
            flex: 1,
            paddingHorizontal: 10,
            paddingVertical: 7,
          }}
        />
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Open calendar for ${label}`}
          accessibilityState={{ expanded: openMonth != null }}
          onPress={toggleCalendar}
          style={{
            alignItems: "center",
            backgroundColor: openMonth ? theme.accentSubtle : theme.surface,
            borderColor: openMonth ? theme.accentStrong : theme.borderStrong,
            borderRadius: 8,
            borderWidth: 1,
            height: 34,
            justifyContent: "center",
            width: 34,
          }}
        >
          <LineIcon color={theme.accentStrong} name="appointment" size={16} strokeWidth={2.2} />
        </Pressable>
      </View>
      {openMonth ? (
        <View
          style={{
            backgroundColor: theme.backgroundSubtle,
            borderColor: theme.borderSubtle,
            borderRadius: 10,
            borderWidth: 1,
            gap: 4,
            marginTop: 6,
            maxWidth: 320,
            padding: 8,
          }}
        >
          <View
            style={{
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Previous month"
              onPress={() => shiftMonth(-1)}
              style={{ paddingHorizontal: 8, paddingVertical: 2 }}
            >
              <Text style={{ color: theme.accentStrong, fontSize: 13, fontWeight: "800" }}>
                {"‹"}
              </Text>
            </Pressable>
            <Text
              style={{
                color: theme.textPrimary,
                fontSize: 12,
                fontWeight: "800",
                lineHeight: 15,
              }}
            >
              {formatMonthTitleText(openMonth.year, openMonth.monthIndex)}
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Next month"
              onPress={() => shiftMonth(1)}
              style={{ paddingHorizontal: 8, paddingVertical: 2 }}
            >
              <Text style={{ color: theme.accentStrong, fontSize: 13, fontWeight: "800" }}>
                {"›"}
              </Text>
            </Pressable>
          </View>
          <View style={{ flexDirection: "row" }}>
            {["S", "M", "T", "W", "T", "F", "S"].map((weekday, index) => (
              <Text
                key={`weekday-${String(index)}`}
                style={{
                  color: theme.textTertiary,
                  flex: 1,
                  fontSize: 9,
                  fontWeight: "700",
                  lineHeight: 12,
                  textAlign: "center",
                }}
              >
                {weekday}
              </Text>
            ))}
          </View>
          {(calendarWeeks ?? []).map((week, weekIndex) => (
            <View key={`week-${String(weekIndex)}`} style={{ flexDirection: "row" }}>
              {week.map((cell) => {
                const isSelected = cell.isoDate === selectedIso;

                return (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={`Select ${cell.isoDate}`}
                    accessibilityState={{ selected: isSelected }}
                    key={cell.isoDate}
                    onPress={() => pickDay(cell.isoDate)}
                    style={{
                      alignItems: "center",
                      backgroundColor: isSelected ? theme.accent : "transparent",
                      borderRadius: 6,
                      flex: 1,
                      paddingVertical: 4,
                    }}
                  >
                    <Text
                      style={{
                        color: isSelected
                          ? theme.accentContrast
                          : cell.inMonth
                            ? theme.textPrimary
                            : theme.textTertiary,
                        fontSize: 10.5,
                        fontWeight: isSelected ? "800" : cell.inMonth ? "600" : "400",
                        lineHeight: 14,
                      }}
                    >
                      {cell.dayNumber}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

export interface QuestionnaireSelectProps {
  label: string;
  options: QuestionnaireSelectOption[];
  selected: QuestionnaireSelectValue[];
  multi: boolean;
  canAddUnlisted: boolean;
  theme: AppShellTheme;
  onChange: (selected: QuestionnaireSelectValue[]) => void;
  testID?: string;
}

export function QuestionnaireSelect(props: QuestionnaireSelectProps): React.JSX.Element {
  const { label, options, selected, multi, canAddUnlisted, theme, onChange, testID } = props;
  const [query, setQuery] = useState("");
  const matches = useMemo(
    () => filterQuestionnaireSelectOptions(query, options),
    [query, options],
  );
  const selectedValues = new Set(selected.map((entry) => entry.value));

  const toggle = (value: string, unlisted: boolean) => {
    if (selectedValues.has(value)) {
      onChange(selected.filter((entry) => entry.value !== value));
      return;
    }
    const next = { value, unlisted };
    onChange(multi ? [...selected, next] : [next]);
    setQuery("");
  };

  const trimmed = query.trim();
  const showAddRow =
    canAddUnlisted &&
    trimmed.length > 0 &&
    !options.some((option) => option.label.toLowerCase() === trimmed.toLowerCase());

  return (
    <View testID={testID} accessibilityLabel={label}>
      <Text style={{ fontWeight: "600", marginBottom: 4, color: theme.textPrimary }}>{label}</Text>
      <TextInput
        value={query}
        onChangeText={setQuery}
        placeholder="Type to search…"
        accessibilityLabel={`${label} search`}
        placeholderTextColor={theme.textTertiary}
        style={{
          borderWidth: 1,
          borderColor: theme.borderStrong,
          backgroundColor: theme.surface,
          color: theme.textPrimary,
          borderRadius: 8,
          paddingHorizontal: 10,
          paddingVertical: 6,
          marginBottom: 6,
        }}
      />
      {selected.length > 0 ? (
        <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 6 }}>
          {selected.map((entry) => (
            <Pressable
              key={entry.value}
              accessibilityRole="button"
              accessibilityLabel={`Remove ${entry.value}`}
              onPress={() => toggle(entry.value, entry.unlisted)}
              style={{
                backgroundColor: entry.unlisted
                  ? theme.statusChip.warning.background
                  : theme.accentSubtle,
                borderWidth: 1,
                borderColor: entry.unlisted
                  ? theme.statusChip.warning.border
                  : theme.accentStrong,
                borderRadius: 12,
                paddingHorizontal: 10,
                paddingVertical: 4,
                marginRight: 6,
                marginBottom: 4,
              }}
            >
              <Text
                style={{
                  color: entry.unlisted ? theme.statusChip.warning.text : theme.accentStrong,
                }}
              >
                {entry.value}
                {entry.unlisted ? " (unlisted)" : ""} ✕
              </Text>
            </Pressable>
          ))}
        </View>
      ) : null}
      {matches.map((option) => (
        <Pressable
          key={option.value}
          accessibilityRole="button"
          onPress={() => toggle(option.value, false)}
          style={{
            paddingVertical: 6,
            paddingHorizontal: 8,
            backgroundColor: selectedValues.has(option.value)
              ? theme.accentSubtle
              : "transparent",
            borderRadius: 6,
          }}
        >
          <Text style={{ color: theme.textPrimary }}>{option.label}</Text>
        </Pressable>
      ))}
      {showAddRow ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Add unlisted option ${trimmed}`}
          onPress={() => toggle(trimmed, true)}
          style={{ paddingVertical: 6, paddingHorizontal: 8 }}
        >
          {/* Saves to this patient immediately; joins the shared list only
              after admin approval (unlisted-option queue). */}
          <Text style={{ color: theme.accentStrong, fontWeight: "600" }}>+ Add “{trimmed}”</Text>
        </Pressable>
      ) : null}
    </View>
  );
}
